import {
  collection,
  DocumentData,
  limit,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  doc,
  deleteDoc,
  addDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { RefundCheck } from '@/types/refund.type';
import { REFUNDSTATUS } from '@/constants/refund.const';
import { GlobalPayment } from '@/types/payment.type';
import { PAYMENT_STATUS } from '@/constants/payment.const';

export function refundCheckDataSubscribe(
  userId: string,
  tabletId: string,
  callback: (refundData: RefundCheck[]) => void,
) {
  try {
    const q = query(
      collection(db, 'refund_check', userId, tabletId),
      orderBy('createdAt', 'desc'),
      limit(1),
    );

    return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const refundInfo = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as RefundCheck),
      );
      console.log('Received refund info:', refundInfo);
      callback(refundInfo);
    });
  } catch (error) {
    console.error('Error subscribing to data:', error);
    throw error;
  }
}

export function globalPaymentDataSubscribe(
  userId: string,
  tabletId: string,
  callback: (refundData: GlobalPayment[]) => void,
) {
  try {
    const q = query(
      collection(db, 'global_payment', userId, tabletId),
      orderBy('createdAt', 'desc'),
      limit(1),
    );

    return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const paymentInfo = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as GlobalPayment),
      );
      console.log('Received payment info:', paymentInfo);
      callback(paymentInfo);
    });
  } catch (error) {
    console.error('Error subscribing to data:', error);
    throw error;
  }
}

export async function deleteRefund(
  userId: string,
  tabletId: string,
  refundId: string,
) {
  try {
    const refundDocRef = doc(db, 'refund_check', userId, tabletId, refundId);
    await deleteDoc(refundDocRef);
    console.log(`Refund with ID ${refundId} has been deleted.`);
  } catch (error) {
    console.error('Error deleting refund:', error);
    throw error;
  }
}

export async function createRefundCheck(
  userId: string,
  tabletId: string,
  totalAmount: number,
  refundAmount: number,
  finalAmount: number,
  isDirectPayment: boolean = false,
  defaultStatus: string = REFUNDSTATUS.PENDING,
) {
  try {
    const refundCollectionRef = collection(
      db,
      'refund_check',
      userId,
      tabletId,
    );

    // Check if there is any existing document in the collection
    const q = query(refundCollectionRef, limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      console.log(
        'A refund already exists. Deleting the existing refund before creating a new one.',
      );
      const existingRefundId = querySnapshot.docs[0].id;
      await deleteRefund(userId, tabletId, existingRefundId);
      console.log(`Existing refund ${existingRefundId} deleted successfully.`);
    }

    const newRefund = {
      createdAt: new Date(),
      status: defaultStatus,
      totalAmount,
      refundAmount,
      finalAmount,
      isDirectPayment,
    };
    const docRef = await addDoc(refundCollectionRef, newRefund);
    console.log('Refund created with ID:', docRef.id);
    console.log('Creating refund...');
  } catch (error) {
    console.error('Error creating refund:', error);
    throw error;
  }
}

export async function updateRefundCheckState(
  userId: string,
  tabletId: string,
  refundId: string,
  newState: string,
) {
  try {
    const refundDocRef = doc(db, 'refund_check', userId, tabletId, refundId);
    await updateDoc(refundDocRef, { status: newState });
    console.log(
      `Refund with ID ${refundId} has been updated to state ${newState}.`,
    );
  } catch (error) {
    console.error('Error updating refund state:', error);
    throw error;
  }
}

export async function updateGlobalPayment(
  userId: string,
  tabletId: string,
  paymentId: string,
  newState: string,
  paymentAmount: number,
  refundId?: string,
  cashAmount?: number,
  cardAmount?: number,
) {
  try {
    const refundDocRef = doc(db, 'global_payment', userId, tabletId, paymentId);

    const updateData: any = {
      status: newState,
      paymentAmount,
    };

    // refundId가 undefined가 아니면 추가
    if (refundId !== undefined) {
      updateData.refundId = refundId;
    }
    // cashAmount가 undefined가 아니면 추가
    if (cashAmount !== undefined) {
      updateData.cashAmount = cashAmount;
    }
    // cardAmount가 undefined가 아니면 추가
    if (cardAmount !== undefined) {
      updateData.cardAmount = cardAmount;
    }

    await updateDoc(refundDocRef, updateData);

    console.log(
      `Payment with ID ${paymentId} has been updated to state ${newState} amount ${paymentAmount}.`,
    );
  } catch (error) {
    console.error('Error updating payment state:', error);
    throw error;
  }
}

export async function updateTabletList(userId: string, newTablets: string[]) {
  try {
    const tabletDocRef = doc(db, 'tablet_list', userId);
    const docSnap = await getDoc(tabletDocRef);

    if (!docSnap.exists()) {
      // 문서가 없으면 새로 생성
      await setDoc(tabletDocRef, { tablets: newTablets });
    } else {
      // 문서가 있으면 업데이트 (배열 병합)
      await updateDoc(tabletDocRef, {
        tablets: arrayUnion(...newTablets),
      });
    }
  } catch (error) {
    console.error('Error updating tablet list:', error);
    throw error;
  }
}

export async function getTabletList(userId: string) {
  try {
    console.log('Fetching tablet list...');
    const tabletDocRef = doc(db, 'tablet_list', userId);
    const tabletDocSnap = await getDoc(tabletDocRef);
    console.log('Tablet list fetched successfully', tabletDocSnap);
    if (!tabletDocSnap.exists()) {
      return ['1'];
    }

    const data = tabletDocSnap.data();
    return data.tablets || ['1'];
  } catch (error) {
    console.error('Error fetching tablet list:', error);
    return ['1'];
  }
}
export const moveRefundCheckDocument = async (
  userId: string,
  prevTabletId: string,
  nextTabletID: string,
) => {
  try {
    const sourceCollectionRef = collection(
      db,
      'refund_check',
      userId,
      prevTabletId,
    );
    const destinationCollectionRef = collection(
      db,
      'refund_check',
      userId,
      nextTabletID,
    );

    // 기존 문서 데이터 가져오기 (첫 번째 요소만)
    const sourceQuery = query(
      sourceCollectionRef,
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const sourceSnapshot = await getDocs(sourceQuery);

    if (!sourceSnapshot.empty) {
      const sourceDoc = sourceSnapshot.docs[0];
      const data = sourceDoc.data(); // 문서 데이터 가져오기

      // 새 위치에 문서가 비어있는지 확인
      const destinationQuery = query(destinationCollectionRef, limit(1));
      const destinationSnapshot = await getDocs(destinationQuery);

      if (destinationSnapshot.empty) {
        // 새 위치에 문서 저장
        await setDoc(doc(destinationCollectionRef, sourceDoc.id), data);

        // 기존 문서 삭제
        await deleteDoc(sourceDoc.ref);
        console.log('문서가 성공적으로 이동되었습니다.');
      } else {
        console.log('새 위치에 이미 문서가 존재합니다.');
      }
    } else {
      console.log('이동할 문서가 존재하지 않습니다.');
    }
  } catch (error) {
    console.error('문서 이동 중 오류 발생:', error);
  }
};

export async function createGlobalPayment(
  userId: string,
  tabletId: string,
  totalPaymentAmount?: number,
  totalPrice?: number,
  isDirectPayment = false,
) {
  try {
    const paymentCollectionRef = collection(
      db,
      'global_payment',
      userId,
      tabletId,
    );

    // Check if there is any existing document in the collection
    const q = query(paymentCollectionRef, limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      console.log(
        'A payment already exists. Deleting the existing payment before creating a new one.',
      );
      const existingPaymentId = querySnapshot.docs[0].id;
      await deleteGlobalPayment(userId, tabletId, existingPaymentId);
      console.log(
        `Existing payment ${existingPaymentId} deleted successfully.`,
      );
    }

    const newPayment = {
      createdAt: new Date(),
      status: PAYMENT_STATUS.CHOOSING,
      totalPaymentAmount: totalPaymentAmount || 0,
      totalPrice: totalPrice || 0,
      isDirectPayment, // 전액결제건
    };
    const docRef = await addDoc(paymentCollectionRef, newPayment);
    console.log('payment created with ID:', docRef.id);
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
}

export async function createGlobalPaymentImmediate(
  userId: string,
  tabletId: string,
  paymentAmount: number,
) {
  try {
    const paymentCollectionRef = collection(
      db,
      'global_payment',
      userId,
      tabletId,
    );

    // Check if there is any existing document in the collection
    const q = query(paymentCollectionRef, limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      console.log(
        'A payment already exists. Deleting the existing payment before creating a new one.',
      );
      const existingPaymentId = querySnapshot.docs[0].id;
      await deleteGlobalPayment(userId, tabletId, existingPaymentId);
      console.log(
        `Existing payment ${existingPaymentId} deleted successfully.`,
      );
    }

    const newPayment = {
      createdAt: new Date(),
      status: PAYMENT_STATUS.SENT,
      paymentAmount,
      isGlobalPaymentOnly: true, // 해외간편결제만 진행함
    };
    const docRef = await addDoc(paymentCollectionRef, newPayment);
    console.log('payment created with ID:', docRef.id);
    return docRef;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
}

export async function deleteGlobalPayment(
  userId: string,
  tabletId: string,
  paymentId: string,
) {
  try {
    const refundDocRef = doc(db, 'global_payment', userId, tabletId, paymentId);
    await deleteDoc(refundDocRef);
    console.log(`Payment with ID ${paymentId} has been deleted.`);
  } catch (error) {
    console.error('Error deleting payment:', error);
    throw error;
  }
}
